import { useEffect } from 'react'
import type { WPPost } from '../../api/wordpress'

// Global flag — persists across all component instances
let hasCompletedInitialHydration = false

interface BlogSEOProps {
  post?: WPPost | null
  type: 'list' | 'post'
}

interface Author {
  name: string
  bio?: string
  avatar?: string
  url?: string
  linkedin?: string | null
  twitter?: string | null
  github?: string | null
  job_title?: string | null
  company?: string
}

const SITE_NAME = 'Wedsy'
const SITE_URL = 'https://wedsy.co'
const BLOG_URL = 'https://wedsy.co/blog'
const DEFAULT_IMAGE = 'https://wedsy.co/og-image.jpg'
const ORGANIZATION_LOGO = 'https://wedsy.co/logo.png'

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').trim()
}

function truncate(str: string, length: number): string {
  if (str.length <= length) return str
  return str.slice(0, length).trim() + '...'
}

function getReadingTime(post: WPPost): number {
  if (post.reading_time) return post.reading_time
  const content = stripHtml(post.content.rendered)
  return Math.ceil(content.split(/\s+/).length / 200)
}

function getAuthor(post: WPPost): Author {
  if (post.author_details) return post.author_details as Author
  const embedded = post._embedded?.author?.[0]
  return {
    name: embedded?.name || 'Wedsy',
    avatar: embedded?.avatar_urls?.['96'],
  }
}

function getFeaturedImage(post: WPPost): string {
  if (post.featured_image_url) return post.featured_image_url
  const media = post._embedded?.['wp:featuredmedia']?.[0]
  return media?.source_url || DEFAULT_IMAGE
}

export function BlogSEO({ post, type }: BlogSEOProps) {
  useEffect(() => {
    // On first render, check if SSR meta tags exist
    const ssrMetaExists = document.querySelector('meta[data-ssr="true"]') !== null

    // Skip on initial hydration — SSR tags are already correct
    if (ssrMetaExists && !hasCompletedInitialHydration) {
      hasCompletedInitialHydration = true
      return
    }

    // Client-side navigation: remove old SSR tags
    document.querySelectorAll('meta[data-ssr="true"]').forEach(el => el.remove())
    document.querySelectorAll('link[rel="canonical"]').forEach(el => {
      if (!el.hasAttribute('data-blog-seo')) el.remove()
    })
    document.querySelectorAll('meta[property^="og:"]').forEach(el => {
      if (!el.hasAttribute('data-blog-seo')) el.remove()
    })
    document.querySelectorAll('meta[name^="twitter:"]').forEach(el => {
      if (!el.hasAttribute('data-blog-seo')) el.remove()
    })
    const defaultDesc = document.querySelector('meta[name="description"]:not([data-blog-seo])')
    if (defaultDesc) defaultDesc.remove()

    // Remove any leftover blog-specific tags from previous navigation
    document.querySelectorAll('[data-blog-seo]').forEach(el => el.remove())

    const cleanup = () => {
      document.querySelectorAll('[data-blog-seo]').forEach(el => el.remove())
    }

    const addMeta = (name: string, content: string, property = false) => {
      const meta = document.createElement('meta')
      meta.setAttribute(property ? 'property' : 'name', name)
      meta.content = content
      meta.setAttribute('data-blog-seo', 'true')
      document.head.appendChild(meta)
    }

    const addLink = (rel: string, href: string) => {
      const link = document.createElement('link')
      link.rel = rel
      link.href = href
      link.setAttribute('data-blog-seo', 'true')
      document.head.appendChild(link)
    }

    const addSchema = (schema: object) => {
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.textContent = JSON.stringify(schema)
      script.setAttribute('data-blog-seo', 'true')
      document.head.appendChild(script)
    }

    if (type === 'list') {
      const title = `Wedding Planning Blog — ${SITE_NAME}`
      const description = 'Tips, inspiration, and advice for planning your perfect wedding. From invitation design to guest management.'

      document.title = title

      addMeta('description', description)
      addMeta('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1')

      addMeta('og:type', 'website', true)
      addMeta('og:site_name', SITE_NAME, true)
      addMeta('og:title', title, true)
      addMeta('og:description', description, true)
      addMeta('og:url', BLOG_URL, true)
      addMeta('og:image', DEFAULT_IMAGE, true)
      addMeta('og:image:width', '1200', true)
      addMeta('og:image:height', '630', true)
      addMeta('og:locale', 'en_GB', true)

      addMeta('twitter:card', 'summary_large_image')
      addMeta('twitter:title', title)
      addMeta('twitter:description', description)
      addMeta('twitter:image', DEFAULT_IMAGE)

      addLink('canonical', BLOG_URL)

      addSchema({
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        name: SITE_NAME,
        url: SITE_URL,
        publisher: {
          '@type': 'Organization',
          '@id': `${SITE_URL}/#organization`,
          name: SITE_NAME,
          url: SITE_URL,
          logo: { '@type': 'ImageObject', url: ORGANIZATION_LOGO, width: 512, height: 512 },
        },
      })

      addSchema({
        '@context': 'https://schema.org',
        '@type': 'Blog',
        '@id': `${BLOG_URL}/#blog`,
        name: `${SITE_NAME} Blog`,
        description,
        url: BLOG_URL,
        isPartOf: { '@id': `${SITE_URL}/#website` },
        publisher: { '@id': `${SITE_URL}/#organization` },
        inLanguage: 'en-GB',
      })

    } else if (type === 'post' && post) {
      const yoast = post.yoast_meta
      const title = stripHtml(post.title.rendered)
      const excerpt = truncate(stripHtml(post.excerpt.rendered), 160)
      const seoTitle = yoast?.title || post.seo_meta?.title || `${title} — ${SITE_NAME} Blog`
      const description = yoast?.description || post.seo_meta?.description || excerpt
      const ogTitle = yoast?.og_title || title
      const ogDescription = yoast?.og_description || description
      const ogImage = yoast?.og_image || post.seo_meta?.og_image || getFeaturedImage(post)
      const twitterTitle = yoast?.twitter_title || ogTitle
      const twitterDescription = yoast?.twitter_description || ogDescription
      const twitterImage = yoast?.twitter_image || ogImage
      const author = getAuthor(post)
      const readingTime = getReadingTime(post)
      const postUrl = yoast?.canonical || `${BLOG_URL}/${post.slug}`

      document.title = seoTitle

      addMeta('description', description)
      addMeta('author', author.name)
      addMeta('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1')

      addMeta('article:published_time', post.date, true)
      addMeta('article:modified_time', post.modified, true)
      addMeta('article:author', author.linkedin || author.name, true)
      addMeta('article:section', 'Wedding Planning', true)

      addMeta('og:type', 'article', true)
      addMeta('og:site_name', SITE_NAME, true)
      addMeta('og:title', ogTitle, true)
      addMeta('og:description', ogDescription, true)
      addMeta('og:url', postUrl, true)
      addMeta('og:image', ogImage, true)
      addMeta('og:image:width', '1200', true)
      addMeta('og:image:height', '630', true)
      addMeta('og:image:alt', ogTitle, true)
      addMeta('og:locale', 'en_GB', true)

      addMeta('twitter:card', 'summary_large_image')
      addMeta('twitter:title', twitterTitle)
      addMeta('twitter:description', twitterDescription)
      addMeta('twitter:image', twitterImage)
      addMeta('twitter:image:alt', twitterTitle)
      addMeta('twitter:label1', 'Reading time')
      addMeta('twitter:data1', `${readingTime} min read`)
      addMeta('twitter:label2', 'Written by')
      addMeta('twitter:data2', author.name)

      addLink('canonical', postUrl)

      const authorSameAs = [author.linkedin, author.twitter, author.github].filter(Boolean) as string[]
      const authorId = author.linkedin || author.url || `${SITE_URL}/authors/${post.slug.split('-').slice(-1)[0]}`

      addSchema({
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        '@id': `${postUrl}#article`,
        headline: title,
        description,
        url: postUrl,
        image: { '@type': 'ImageObject', url: ogImage, width: 1200, height: 630 },
        datePublished: post.date,
        dateModified: post.modified,
        wordCount: stripHtml(post.content.rendered).split(/\s+/).length,
        timeRequired: `PT${readingTime}M`,
        mainEntityOfPage: { '@type': 'WebPage', '@id': postUrl },
        isPartOf: { '@id': `${BLOG_URL}/#blog` },
        author: {
          '@type': 'Person',
          '@id': authorId,
          name: author.name,
          url: author.url || author.linkedin || undefined,
          description: author.bio || undefined,
          image: author.avatar ? { '@type': 'ImageObject', url: author.avatar } : undefined,
          jobTitle: author.job_title || undefined,
          worksFor: { '@type': 'Organization', '@id': `${SITE_URL}/#organization`, name: SITE_NAME },
          sameAs: authorSameAs.length > 0 ? authorSameAs : undefined,
        },
        publisher: { '@id': `${SITE_URL}/#organization` },
        inLanguage: 'en-GB',
      })

      addSchema({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: BLOG_URL },
          { '@type': 'ListItem', position: 3, name: title, item: postUrl },
        ],
      })
    }

    return cleanup
  }, [post, type])

  return null
}
